import { gql } from "@apollo/client";
import {
  ITEMBASICSFRAGMENT,
  REVIEWFEEDFRAGMENT,
  FEATUREDIMAGETHUMBNAILFRAGMENT,
  ATHLETE_RACE_TAGGING_FRAGMENT,
} from "@geerly/shared";

import { TERMFRAGMENT, ATHLETE_BASICS_FRAGMENT } from "graphql/fragments";
import { FEEDBLOCKFRAGMENT } from "components/Feedblocks/FeedBlock";
import { ATHLETE_FRAGMENT } from "../components/ProductAthletes/ProductAthletes";

/*  */

const TERRAIN_FRAGMENT = gql`
  fragment TerrainFragment on Terrain {
    name
    id
    slug
    termDetails {
      featuredTermForSports {
        id
      }
    }
  }
`;

const ATHLETE_ITEM_FRAGMENT = gql`
  fragment AthleteItemFragment on Item {
    id
    databaseId
    date
    itemStats {
      distance
      averages {
        speed
        cadence
        count
        elevationGain
        heartrate
        breakdowns {
          racing {
            cumulativeDistance
            cadence
            speed
          }
          longRuns {
            cumulativeDistance
          }
          workouts {
            cumulativeDistance
          }
        }
      }
    }
    itemDetails {
      description
      stravaDefault
      integrationId
      modelName
      productSelectedManually
      new
      retired
    }
  }
`;

const ATHLETE_REVIEW_FRAGMENT = gql`
  fragment AthleteReviewFragment on Review {
    id
    date
    reviewColour
    reviewDetails {
      dateIsExplicit
      scoreIsExplicit
      reviewScore
      ratings {
        ratingCriteria {
          ... on Attribute {
            id
            slug
            title
          }
        }
        score
      }
      privacy
      reviewedProducts {
        ... on Product {
          id
          uri
          title
        }
      }
      videoReview {
        url
        duration
        embeddable
        viewCount
        thumbnails {
          high {
            url
          }
        }
      }
      writtenReview {
        pageUrl
        review
        excerpt
        timeToRead
      }
      reviewHeadline
      publisher {
        ... on NodeWithTitle {
          title
        }
        ... on Node {
          id
        }
      }
    }
  }
`;

export const ATHLETE_QUERY_PUBLIC_FRAGMENT = gql`
  fragment AthletePublicFragment on Athlete {
    ...AthleteBasicsFragment
    athleteProducts(
      # TODO: Should allow pagination here. Around 50 can cause memory issues, so playing it safe for now
      first: 25
      where: { hideProductsWatched: $hideProductsWatched }
    ) {
      edges {
        followingWithProduct {
          pageInfo {
            endCursor
            startCursor
            offsetPagination {
              total
            }
          }
          edges {
            node {
              ...AthleteFragment
            }
          }
        }
        product: node {
          ... on ContentNode {
            ...ItemBasicsFragment
            ...FeedBlockFragment
            ...FeaturedImageThumbnailFragment
          }
          ... on ProductNode {
            # Should be moved to separate query? not used by privateQuery
            ...ReviewFeedFragment
          }
          ... on Product {
            sports {
              edges {
                isPrimary
                node {
                  id
                  slug
                  name
                }
              }
            }
            latestModelDetails {
              isLatestModel
            }
            family {
              node {
                ...ItemBasicsFragment
                latestModel {
                  node {
                    ...ItemBasicsFragment
                    noBrandTitle
                  }
                }
                sports {
                  edges {
                    isPrimary
                    node {
                      id
                      slug
                      name
                    }
                  }
                }
                category {
                  node {
                    ...ItemBasicsFragment
                    name
                    categoryDetails {
                      singleName
                    }
                  }
                }
              }
            }
            terrains {
              edges {
                isPrimary
                node {
                  ...TerrainFragment
                }
              }
            }
          }
          ... on Family {
            terrains {
              edges {
                isPrimary
                node {
                  ...TerrainFragment
                }
              }
            }
            featuredImage {
              node {
                id
                altText
                sourceUrl(size: THUMBNAIL)
              }
            }
            sports {
              edges {
                isPrimary
                node {
                  id
                  slug
                  name
                }
              }
            }
          }
          ... on ProductNode {
            category {
              node {
                id
                databaseId
                slug
                name
                categoryDetails {
                  singleName
                  supportedChoiceTypes
                  fallbackImage {
                    sourceUrl
                    altText
                    id
                  }
                }
              }
            }
            brand {
              uri
              node {
                id
                slug
                name
              }
            }
            priceBand {
              node {
                slug
                id
              }
            }
          }
        }
        items {
          ...AthleteItemFragment
        }
        settings {
          distanceAlertThreshold
          distanceAlerts
          priceAlertThreshold
          priceAlerts
          familyPriceAlerts
          newModelAlerts
          newReviewAlerts
          shoeSize
        }
        usageStatus
        # could use (limit: 1, orderByViewsCount: true)
        reviews(privacy: PUBLIC, orderByViewsCount: true) {
          reviewsCount
          publicReviews {
            ...AthleteReviewFragment
          }
        }
      }
    }
    athleteUnmatchedItems {
      nodes {
        ...AthleteItemFragment
      }
    }
  }
  ${REVIEWFEEDFRAGMENT}
  ${ATHLETE_ITEM_FRAGMENT}
  ${ATHLETE_REVIEW_FRAGMENT}
  ${TERRAIN_FRAGMENT}
  ${FEEDBLOCKFRAGMENT}
  ${ATHLETE_FRAGMENT}
  ${ATHLETE_BASICS_FRAGMENT}
`;

export const ATHLETE_RECOMMENDATIONS_QUERY = gql`
  query AthleteRecommendationsQuery(
    $slug: ID!
    $category: ID!
    $filterTerms: [FilterTermsInputObject]
  ) {
    athlete(id: $slug, idType: SLUG) {
      recommendations(
        productType: PRODUCT
        limit: 6
        category: $category
        filterTerms: $filterTerms
      ) {
        productNodes {
          nodes {
            similarityWeighting
            node {
              ...ItemBasicsFragment
              ...FeaturedImageThumbnailFragment
            }
          }
        }
        hasMoreResults
        node {
          ... on TermContentNode {
            safeLowerCasedName
          }
        }
      }
    }
  }
  ${ITEMBASICSFRAGMENT}
  ${FEATUREDIMAGETHUMBNAILFRAGMENT}
`;

// Removed section:
/* similarProducts(
  productType: INHERIT
  includeSecondaryCharacteristics: true
  showFeaturedPrimaryTerms: false
  limit: 5
  termLimit: 3
) {
  terms {
    edges {
      uri
      node {
        ... on TermNode {
          slug
          name
        }
        ... on TermContentNode {
          safeLowerCasedName
        }
      }
    }
  }
} */

export const ATHLETE_TAGGING_PREFERENCES_FRAGMENT = gql`
  fragment AthleteTaggingPreferencesFragment on Athlete {
    taggingPreferences {
      parkrunStatsSync
      parkrunId
      hidePosition
      tagParkrunStreaks
      tagReviews
      tagNewGear
      tagYearProgress {
        running
        cycling
        swimming
      }
      tagStreaks {
        running
        cycling
        swimming
      }
      tagYearTargets
      yearTargetRuns
      yearTargetRides
      yearTargetSwims
      monthTargetRuns
      monthTargetRides
      monthTargetSwims
      hideShortRuns
      shortRunThreshold
    }
  }
`;

export const ATHLETE_QUERY_PUBLIC = gql`
  query AthleteQueryPublic($id: ID!, $hideProductsWatched: Boolean) {
    athlete(id: $id, idType: SLUG) {
      ...AthletePublicFragment
    }
  }
  ${ATHLETE_QUERY_PUBLIC_FRAGMENT}
`;

export const ATHLETE_QUERY_PRIVATE = gql`
  query AthleteQueryPrivate($id: ID!, $hideProductsWatched: Boolean) {
    athlete(id: $id, idType: SLUG) {
      ...AthletePublicFragment
      hasEmail
      athleteCharacteristics {
        pronationType {
          ...TermFragment
        }
        strikeType {
          ...TermFragment
        }
        archType {
          ...TermFragment
        }
        favouriteBrands {
          ...TermFragment
        }
        weight
        sex {
          ... on Enum {
            slug
          }
        }
      }
      athleteDetails {
        birthday
      }
      ...AthleteTaggingPreferencesFragment
      communicationPreferences {
        doNotRequestEmail
        email
        emailVerified
        emailCommunications
        distanceAlertThreshold
        distanceAlerts
        priceAlerts
        priceAlertThresholdReduction
        familyPriceAlerts
        newModelAlerts
        sendParkrunResults
        # newFollowerAlerts
        # newReviewAlerts
        #  reviewRequestAlert
        measurementPreference
        pushNotifications
        hasOnboarded
        hasHadTour
        shoeSizing {
          size
          sizeLocale {
            ... on Unit {
              ...ItemBasicsFragment
            }
          }
          brand {
            ...ItemBasicsFragment
            databaseId
            name
            slug
          }
          sex {
            ... on Enum {
              ...ItemBasicsFragment
            }
          }
        }
      }
      athleteStats {
        calculated {
          distancePerWeek {
            overall
          }
        }

        stats {
          running {
            averages {
              speed
              cadence
              heartrate
              breakdowns {
                racing {
                  speed
                  cadence
                }
              }
            }
          }
        }
      }
      followers {
        pageInfo {
          offsetPagination {
            total
          }
        }
      }
      following {
        nodes {
          id
          databaseId
          slug
          title
        }
        pageInfo {
          offsetPagination {
            total
          }
        }
      }
    }
  }
  ${ATHLETE_QUERY_PUBLIC_FRAGMENT}
  ${ATHLETE_TAGGING_PREFERENCES_FRAGMENT}
  ${TERMFRAGMENT}
`;

const ATHLETE_TO_DATE_STATS_FRAGMENT = gql`
  fragment AthleteToDateStatsFragment on Athlete {
    athleteStats {
      toDate {
        lastUpdated
        running {
          streak
          lastUpdated
          year {
            count
            elevationGain
            distance
          }
          month {
            count
            elevationGain
            distance
            time
          }
          week {
            count
            elevationGain
            distance
            time
          }
        }
        cycling {
          streak
          lastUpdated
          year {
            count
            elevationGain
            distance
            time
          }
          month {
            count
            elevationGain
            distance
            time
          }
          week {
            count
            elevationGain
            distance
            time
          }
        }
        swimming {
          streak
          lastUpdated
          year {
            count
            elevationGain
            distance
            time
          }
          month {
            count
            elevationGain
            distance
            time
          }
          week {
            count
            elevationGain
            distance
            time
          }
        }
      }
    }
  }
`;

export const ATHLETE_GEAR_FRAGMENT = gql`
  fragment AthleteGearFragment on Athlete {
    gear {
      items {
        ... on Item {
          id
          itemStats {
            distance
          }
          itemDetails {
            integrationId
            # TODO: This is all only needed for back end, should be split out so not pulled on client queries
            linkedProduct {
              ... on NodeWithTitle {
                title
              }
              ... on ContentNode {
                id
                databaseId
                slug
              }
              ... on ProductNode {
                category {
                  node {
                    id
                    name
                    slug
                    categoryDetails {
                      singleName
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const ATHLETE_TAGS_ALL_QUERY = gql`
  query AthleteTagsAllQuery($id: ID!) {
    athlete(id: $id, idType: SLUG) {
      id
      ...AthleteToDateStatsFragment
      ...AthleteGearFragment
    }
  }
  ${ATHLETE_GEAR_FRAGMENT}
  ${ATHLETE_TO_DATE_STATS_FRAGMENT}
`;
export const ATHLETE_GEAR_QUERY = gql`
  query AthleteGearQuery($id: ID!) {
    athlete(id: $id, idType: SLUG) {
      id
      ...AthleteGearFragment
    }
  }
  ${ATHLETE_GEAR_FRAGMENT}
`;

export const ATHLETE_TO_DATE_STATS_QUERY = gql`
  query AthleteToDateStatsQuery($id: ID!) {
    athlete(id: $id, idType: SLUG) {
      id
      ...AthleteToDateStatsFragment
    }
  }
  ${ATHLETE_TO_DATE_STATS_FRAGMENT}
`;
/*
Removed from athlete-query-private
gear {
        items {
          ... on Item {
            id
            itemStats {
              distance
            }
            itemDetails {
              integrationId
              # TODO: This is all only needed for back end, should be split out so not pulled on client queries
              linkedProduct {
                ... on NodeWithTitle {
                  title
                }
                ... on ContentNode {
                  id
                  databaseId
                  slug
                }
                ... on ProductNode {
                  category {
                    node {
                      id
                      name
                      slug
                      categoryDetails {
                        singleName
                      }
                    }
                  }
                  #priceBand {
                  #  node {
                  #    slug
                  #    id
                  #  }
                  #}
                }
              }
            }
          }
        }
      }
      */

export const DYNAMIC_ATHLETE_CONTENT = gql`
  query DynamicAthleteContent($id: ID!, $productId: ID!) {
    athlete(id: $id, idType: SLUG) {
      athleteReviews(where: { productId: $productId, privacy: PUBLIC }) {
        edges {
          node {
            ...AthleteReviewFragment
          }
        }
      }
    }
  }
  ${ATHLETE_REVIEW_FRAGMENT}
`;

export const USER_FOLLOWING_WITH_PRODUCT_QUERY = gql`
  query UserFollowingWithProductQuery($id: ID!, $productIds: [ID!]) {
    __typename
    athlete(id: $id, idType: DATABASE_ID) {
      id
      followingWithProduct: following(where: { productIds: $productIds }) {
        edges {
          node {
            ...AthleteFragment
          }
        }
      }
    }
  }
  ${ATHLETE_FRAGMENT}
  ${FEATUREDIMAGETHUMBNAILFRAGMENT}
`;

export const ATHLETE_PROCESS_TAGS_QUERY = gql`
  query AthleteProcessTagsQuery($id: ID!) {
    athlete(id: $id, idType: SLUG) {
      id
      databaseId
      ...AthleteTaggingPreferencesFragment
      communicationPreferences {
        measurementPreference
      }
      integration {
        isSynched
      }
    }
  }
  ${ATHLETE_TAGGING_PREFERENCES_FRAGMENT}
`;
/* communicationPreferences {
      measurementPreference
    }
    integration {
      platformId
      accountAuthorised
      integrationPlatform {
        ... on Integration {
          id
          slug
        }
      }
    } */

export const ATHLETE_QUERY_POST_EVENT = gql`
  query AthleteQueryPostEvent($id: ID!) {
    athlete(id: $id, idType: DATABASE_ID) {
      slug
      ...AthleteRaceTaggingFragment
    }
  }
  ${ATHLETE_RACE_TAGGING_FRAGMENT}
`;
